"use client"

import { useId, useState } from "react"
import { Formik } from "formik"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { CrmReadyToShipStageField } from "@/components/deliveries/crm-ready-to-ship-stage-field"
import { useAgentDeliverySettingsQuery } from "@/hooks/queries/delivery-bindings"
import { useSaveCrmStatusFallbackMutation } from "@/hooks/mutations/delivery-bindings"
import type { DeliveryScopeType } from "@/lib/deliveries/config"
import { useTranslations } from "next-intl"


export function CrmStatusFallbackSettings({ agentId, scopeType, disabled = false }: {
  agentId: string
  scopeType: DeliveryScopeType
  disabled?: boolean
}) {
  const tUi = useTranslations("Deliveries.DeliveriesCrmStatusFallbackSettings")

  const [open, setOpen] = useState(false)
  const id = useId()
  const settings = useAgentDeliverySettingsQuery(scopeType, agentId)
  const mutation = useSaveCrmStatusFallbackMutation()
  const fallback = settings.data?.crmStatusFallback
  return (
    <>
      <Button type="button" variant="outline" className="h-auto min-h-10 w-full whitespace-normal"
        disabled={disabled || !settings.data} onClick={() => setOpen(true)}>
        <span><span key={tUi("cRMStatusFallback")}>{tUi("cRMStatusFallback")}</span></span> <span key={fallback?.enabled ?? true ? tUi("enabled") : tUi("disabled")}>{fallback?.enabled ?? true ? tUi("enabled") : tUi("disabled")}</span>
      </Button>
      <Dialog open={open} onOpenChange={next => { if (!mutation.isPending) setOpen(next) }}>
        <DialogContent className="max-h-[85dvh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle><span key={tUi("cRMStatusFallback2")}>{tUi("cRMStatusFallback2")}</span></DialogTitle>
            <DialogDescription>
              <span key={tUi("whenWaybillCreationFailsMoveTheCRMLead")}>{tUi("whenWaybillCreationFailsMoveTheCRMLead")}</span></DialogDescription>
          </DialogHeader>
          <Formik enableReinitialize initialValues={{ enabled: fallback?.enabled ?? true, stageId: fallback?.enabled ? (fallback.stageId ?? "") : "" }}
            validate={values => {
              if (values.enabled && values.stageId === settings.data?.effectiveBinding?.crmSignal?.stageId) {
                return { stageId: "Choose a different status from the ready-to-ship status." }
              }
              return {}
            }}
            onSubmit={async (values, helpers) => {
              try {
                await mutation.mutateAsync({
                  scopeType, scopeId: agentId,
                  crmStatusFallback: values.enabled
                    ? (values.stageId.trim() ? { enabled: true, stageId: values.stageId.trim() } : { enabled: true })
                    : { enabled: false },
                  setErrors: errors => helpers.setErrors({ stageId: String(errors["crmStatusFallback.stageId"] ?? errors.stageId ?? "Check the selected CRM status.") }),
                })
                setOpen(false)
              } catch { /* The shared mutation reports save errors. */ }
            }}>
            {({ values, setFieldValue, handleSubmit, isSubmitting }) => (
              <form className="min-w-0 space-y-4" onSubmit={event => { event.stopPropagation(); handleSubmit(event) }}>
                <div className="flex items-start justify-between gap-4 rounded-md border p-4">
                  <div className="min-w-0 space-y-1">
                    <Label htmlFor={id}><span key={tUi("enableCRMStatusFallback")}>{tUi("enableCRMStatusFallback")}</span></Label>
                    <p className="text-sm text-muted-foreground"><span key={tUi("enabledByDefaultLeaveTheStatusEmptyTo")}>{tUi("enabledByDefaultLeaveTheStatusEmptyTo")}</span></p>
                  </div>
                  <Switch id={id} className="shrink-0" checked={values.enabled} disabled={disabled || isSubmitting}
                    onCheckedChange={enabled => void setFieldValue("enabled", enabled)} />
                </div>
                {values.enabled ? <CrmReadyToShipStageField name="stageId" label={tUi("fallbackCRMStatus")}
                  description={tUi("optionalLeaveEmptyToChooseASuitableManual")} scope="agent" disabled={disabled || isSubmitting} /> : null}
                <Button type="submit" className="h-auto min-h-10 w-full whitespace-normal" disabled={disabled || isSubmitting}>
                  <span key={isSubmitting ? tUi("saving") : tUi("saveCRMStatusFallback")}>{isSubmitting ? tUi("saving") : tUi("saveCRMStatusFallback")}</span>
                </Button>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}
